import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products } from '../data/products';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiInfo, FiCheckCircle } from 'react-icons/fi';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.id === id);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-brand-cream">
        <h2 className="text-2xl font-bold text-brand-charcoal mb-4">Product not found</h2>
        <button onClick={() => navigate('/products')} className="text-brand-green font-semibold hover:underline">
          Return to Products
        </button>
      </div>
    );
  }

  return (
    <div className="bg-brand-cream min-h-screen py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/products" className="inline-flex items-center text-sm font-semibold text-brand-charcoal hover:text-brand-green mb-8 transition-colors">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Products
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-brand-sand overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            
            {/* Image Gallery area */}
            <div className="bg-white p-12 flex items-center justify-center border-b md:border-b-0 md:border-r border-brand-sand relative">
              <div className="absolute top-6 left-6">
                <span className="bg-brand-sand text-brand-charcoal text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {product.category}
                </span>
              </div>
              <img 
                src={product.image} 
                alt={product.name} 
                className="max-w-full h-auto max-h-[400px] object-contain drop-shadow-xl"
              />
            </div>

            {/* Product Info */}
            <div className="p-8 md:p-12 flex flex-col">
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-brand-charcoal mb-2">
                {product.name}
              </h1>
              
              <div className="flex items-center text-sm text-gray-500 mb-8 border-b border-gray-100 pb-6">
                <span className="font-semibold text-brand-charcoal mr-2">Package Size:</span> 
                <span className="bg-brand-sand px-2 py-1 rounded text-brand-charcoal">{product.packageSize || 'Various'}</span>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-bold text-brand-charcoal mb-3 flex items-center">
                  Description
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {product.description}
                </p>
              </div>

              {product.suggestedUses && (
                <div className="mb-10 bg-brand-sand/50 p-6 rounded-xl border border-brand-sand">
                  <h3 className="text-lg font-bold text-brand-charcoal mb-3 flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-brand-green" /> Suggested Uses
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {product.suggestedUses}
                  </p>
                </div>
              )}

              <div className="mt-auto pt-8 border-t border-gray-100">
                <div className="flex items-start text-sm text-brand-red bg-red-50 p-4 rounded-lg mb-6">
                  <SafeIcon icon={FiInfo} className="mr-3 mt-0.5 flex-shrink-0" />
                  <p>Availability and price to be confirmed. This is a prototype platform.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to={`/contact?product=${encodeURIComponent(product.name)}`}
                    className="flex-1 bg-brand-green text-center text-white py-4 rounded-md font-bold hover:bg-opacity-90 transition-colors shadow-md"
                  >
                    Request this product
                  </Link>
                  <Link 
                    to="/wholesale"
                    className="flex-1 bg-white border-2 border-brand-charcoal text-center text-brand-charcoal py-4 rounded-md font-bold hover:bg-brand-charcoal hover:text-white transition-colors"
                  >
                    Wholesale Enquiry
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;